// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { PotraceOptions, TraceResult, WorkerOut } from './types';
import { countColors, countPaths, minifySvg, normalizeSvg, svgDimensions } from './svgstats';

export interface TraceProgress {
  phase: 'decode' | 'trace' | 'finish';
  pct: number;
}

interface Pending {
  jobId: number;
  resolve: (r: TraceResult) => void;
  reject: (e: Error) => void;
  onProgress?: (p: TraceProgress) => void;
}

/**
 * Owns the trace worker and multiplexes jobs onto it by id. A newer job
 * supersedes an older one: the stale job's promise rejects with a benign
 * `AbortError`, and late results for it are dropped.
 */
export class Tracer {
  private worker: Worker | null = null;
  private seq = 0;
  private pending: Pending | null = null;

  private spawn(): Worker {
    const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (ev: MessageEvent<WorkerOut>) => this.onMessage(ev.data);
    w.onerror = (ev) => this.onWorkerError(ev.message || 'Trace worker crashed.');
    return w;
  }

  private ensureWorker(): Worker {
    if (!this.worker) this.worker = this.spawn();
    return this.worker;
  }

  private onWorkerError(message: string): void {
    // A crash invalidates the worker; drop it and fail the in-flight job.
    if (this.pending) {
      this.pending.reject(new Error(message));
      this.pending = null;
    }
    this.worker?.terminate();
    this.worker = null;
  }

  private onMessage(msg: WorkerOut): void {
    const p = this.pending;
    if (!p || msg.jobId !== p.jobId) return; // stale — ignore
    if (msg.type === 'progress') {
      p.onProgress?.({ phase: msg.phase, pct: msg.pct });
      return;
    }
    if (msg.type === 'error') {
      p.reject(new Error(msg.message));
      this.pending = null;
      return;
    }
    // done
    const start = perfStart.get(msg.jobId) ?? 0;
    perfStart.delete(msg.jobId);
    const svg = minifySvg(normalizeSvg(msg.svg), 2);
    const dims = svgDimensions(svg);
    p.resolve({
      svg,
      bytes: new Blob([svg]).size,
      paths: countPaths(svg),
      colors: countColors(svg),
      width: dims.width || msg.tracedWidth,
      height: dims.height || msg.tracedHeight,
      tracedWidth: msg.tracedWidth,
      tracedHeight: msg.tracedHeight,
      ms: start ? Math.round(performance.now() - start) : 0,
    });
    this.pending = null;
  }

  /**
   * Trace `blob` with `options`. If another trace is requested first, the older
   * promise rejects with an `AbortError` that callers can safely ignore.
   */
  trace(
    blob: Blob,
    options: PotraceOptions,
    maxDim: number,
    onProgress?: (p: TraceProgress) => void,
  ): Promise<TraceResult> {
    // Supersede any in-flight job.
    if (this.pending) {
      const err = new Error('Superseded by a newer trace.');
      err.name = 'AbortError';
      this.pending.reject(err);
      this.pending = null;
    }
    const jobId = ++this.seq;
    perfStart.set(jobId, performance.now());
    return new Promise<TraceResult>((resolve, reject) => {
      this.pending = { jobId, resolve, reject, onProgress };
      const w = this.ensureWorker();
      w.postMessage({ type: 'trace', jobId, blob, options, maxDim });
    });
  }

  /** Hard-cancel the current job by tearing the worker down. */
  cancel(): void {
    if (this.pending) {
      const err = new Error('Cancelled.');
      err.name = 'AbortError';
      this.pending.reject(err);
      this.pending = null;
    }
    this.worker?.terminate();
    this.worker = null;
  }
}

const perfStart = new Map<number, number>();

export function isAbort(e: unknown): boolean {
  return e instanceof Error && e.name === 'AbortError';
}
